/**
 * usePublicMenu — fetches restaurant menu data for the public menu page.
 * Returns restaurant info, categories, products, combos, theme, and table meta.
 */
import { useState, useCallback } from "react";
import axios from "axios";
import { filterCategories, filterProducts } from "@/utils/dataIntegrity";
import { setLanguageFromLocale, setArabicMenuLocale } from "@/i18n";
import { API_URL } from "@/utils/apiConfig";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const _BACKEND_URL = API_URL || "";
export const PUBLIC_API = `${_BACKEND_URL}/api`;
export const PUBLIC_BACKEND_BASE = _BACKEND_URL;

export function usePublicMenu(restaurantId, tableParam) {
  const { t } = useTranslation();
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [smartPricing, setSmartPricing] = useState(null);
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuError, setMenuError] = useState(false);
  const [isSuspended, setIsSuspended] = useState(false);
  const [suspendedMessage, setSuspendedMessage] = useState("");
  const [tableNumber, setTableNumber] = useState(null);
  const [tableName, setTableName] = useState(null);
  const [tableId, setTableId] = useState(null);
  const [tablePayAtEnd, setTablePayAtEnd] = useState(false);
  const [tableCurrentSessionId, setTableCurrentSessionId] = useState(undefined);
  const [tablePaymentEnabled, setTablePaymentEnabled] = useState(false);
  const [paymentAtTableEnabled, setPaymentAtTableEnabled] = useState(true);
  // RTL: true when restaurant.rtl_enabled=true AND layout_direction="rtl" (gating applied server-side)
  const [isRtl, setIsRtl] = useState(false);

  const fetchMenu = useCallback(async () => {
    setMenuError(false);
    try {
      let url = `${PUBLIC_API}/public/menu/${restaurantId}`;
      if (tableParam) url += `?table=${tableParam}`;
      const menuRes = await axios.get(url, { timeout: 12000 });

      if (menuRes.data.is_closed) {
        setIsSuspended(true);
        setSuspendedMessage(menuRes.data.closed_message || t("publicMenu.closedDefaultMessage"));
        setRestaurant(menuRes.data.restaurant);
        return;
      }

      if (menuRes.data.theme) setTheme(menuRes.data.theme);
      setRestaurant(menuRes.data.restaurant);
      setTablePaymentEnabled(menuRes.data.restaurant?.table_payment_enabled === true);
      setPaymentAtTableEnabled(menuRes.data.restaurant?.payment_at_table_enabled !== false);

      // Arabic + RTL: backend applies feature-flag gating before returning these values.
      // arabic_enabled=true && ui_language="ar" → activate Arabic (explicit tenant decision).
      // rtl_enabled=true && layout_direction="rtl" → apply RTL container on public menu.
      const rest = menuRes.data.restaurant;
      if (rest?.arabic_enabled && rest?.ui_language === "ar") {
        setArabicMenuLocale();
      } else if (rest?.locale) {
        setLanguageFromLocale(rest.locale);
      }
      setIsRtl(rest?.layout_direction === "rtl");
      setCategories(filterCategories(menuRes.data.categories || []));
      setProducts(filterProducts(menuRes.data.products || []));
      setCombos(menuRes.data.combos || []);
      if (menuRes.data.smart_pricing) setSmartPricing(menuRes.data.smart_pricing);
      if (menuRes.data.table_number) setTableNumber(menuRes.data.table_number);
      if (menuRes.data.table_name) setTableName(menuRes.data.table_name);
      if (menuRes.data.table_id) setTableId(menuRes.data.table_id);
      if (menuRes.data.table_pay_at_end) setTablePayAtEnd(true);
      // Use explicit `in` check: undefined = old backend (feature not supported), null = no active session
      setTableCurrentSessionId(
        "table_current_session_id" in menuRes.data
          ? menuRes.data.table_current_session_id
          : undefined
      );
    } catch {
      setMenuError(true);
      // Do NOT set a fake theme — leave theme as null so the page can show a proper error/retry
      try { toast.error(t("publicMenu.errors.loadMenu")); } catch {}
    } finally {
      setLoading(false);
    }
  }, [restaurantId, tableParam, t]);

  return {
    restaurant, categories, products, combos, smartPricing, theme, loading, menuError,
    isSuspended, suspendedMessage, tableNumber, tableName, tableId,
    tablePayAtEnd, tableCurrentSessionId, tablePaymentEnabled, paymentAtTableEnabled,
    isRtl, fetchMenu,
  };
}
